import React, { useState, useEffect } from 'react';
import { jwtDecode as jwt } from 'jwt-decode';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

function AdminDashboard() {
  const [owners, setOwners] = useState([]); // Initialize as an empty array
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showPassword, setShowPassword] = useState({});
  const [formData, setFormData] = useState({
    id: null,
    username: '',
    password: '',
    business_name: '',
    phone: '',
    isActive: true,
  });

  // Fetch owners from the backend
  const fetchOwners = async () => {
    try {
      const response = await fetch('/api/owner');
      const data = await response.json();
      setOwners(data.owners || []); // Ensure owners is always an array
    } catch (error) {
      console.error('Error fetching owners:', error);
      setOwners([]); // Fallback to an empty array in case of an error
    }
  };

  useEffect(() => {
    const verifyTokenAndFetchData = async () => {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        window.location.href = '/admin-login';
        return;
      }
      
      try {
        const decoded = jwt(token);
        console.log('Decoded token:', decoded);
        if (!decoded || decoded.role !== 'admin') {
          console.error('Token validation failed - missing decoded data or invalid role');
          throw new Error('Invalid token');
        }
        await fetchOwners();
      } catch (error) {
        console.error('Token verification failed:', error);
        console.log('Current token:', token);
        localStorage.removeItem('adminToken');
        window.location.href = '/admin-login';
      }
    };
    
    verifyTokenAndFetchData();
  }, []);

  // Handle input changes in the form
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Handle form submission for adding or editing an owner
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing) {
        await fetch(`/api/owner/${formData.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username: formData.username,
            password: formData.password,
            business_name: formData.business_name,
            phone: formData.phone,
          }),
        });
      } else {
        await fetch('/api/owner', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username: formData.username,
            password: formData.password,
            business_name: formData.business_name,
            phone: formData.phone,
          }),
        });
      }
      fetchOwners();
      setShowModal(false);
      setFormData({ id: null, username: '', password: '', business_name: '', phone: '', isActive: true });
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  // Handle editing an owner
  const handleEdit = (owner) => {
    setFormData({
      id: owner.id,
      username: owner.username,
      password: owner.password || '',
      business_name: owner.business_name,
      phone: owner.phone || '',
      isActive: owner.isactive,
    });
    setIsEditing(true);
    setShowModal(true);
  };

  const togglePasswordVisibility = (id) => {
    setShowPassword(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Toggle owner active status
  const handleToggleStatus = async (owner, isChecked) => {
    try {
      await fetch(`/api/owner/${owner.id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isactive: isChecked })
      });
      // Optimistic UI update
      setOwners(prev => prev.map(o => (o.id === owner.id ? { ...o, isactive: isChecked } : o)));
    } catch (error) {
      console.error('Error updating owner status:', error);
    }
  };

  // Handle deleting an owner
  const handleDelete = async (id) => {
    try {
      await fetch(`/api/owner/${id}`, {
        method: 'DELETE',
      });
      fetchOwners();
    } catch (error) {
      console.error('Error deleting owner:', error);
    }
  };

  return (
    <div className="container mt-5">
      <h1>Admin Dashboard</h1>
      <button
        className="btn btn-primary my-3"
        onClick={() => {
          setIsEditing(false);
          setFormData({ id: null, username: '', password: '', business_name: '', phone: '', isActive: true });
          setShowModal(true);
        }}
      >
        Add Owner
      </button>

      {/* Owners Table */}
      <table className="table table-bordered">
        <thead>
          <tr>
            <th>Username</th>
            <th>Business Name</th>
            <th>Phone</th>
            <th>Status</th>
            <th>Password</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {owners.length > 0 ? (
            owners.map((owner) => (
              <tr key={owner.id}>
                <td>{owner.username}</td>
                <td>{owner.business_name}</td>
                <td>{owner.phone}</td>
                <td>
                  <div className="form-check form-switch d-flex align-items-center">
                    <input
                      className="form-check-input me-2"
                      type="checkbox"
                      role="switch"
                      id={`owner-active-${owner.id}`}
                      checked={!!owner.isactive}
                      onChange={(e) => handleToggleStatus(owner, e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor={`owner-active-${owner.id}`}>
                      {owner.isactive ? 'Active' : 'Inactive'}
                    </label>
                  </div>
                </td>
                <td>
                  {owner.password ? (
                    <div className="input-group">
                      <input
                        type={showPassword[owner.id] ? 'text' : 'password'}
                        className="form-control form-control-sm"
                        value={owner.password}
                        readOnly
                        style={{ width: '100px' }}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => togglePasswordVisibility(owner.id)}
                      >
                        {showPassword[owner.id] ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  ) : (
                    'No password'
                  )}
                </td>
                <td>
                  <button className="btn btn-warning btn-sm me-2" onClick={() => handleEdit(owner)}>
                    Edit
                  </button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(owner.id)}>
                    Delete
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="6" className="text-center">
                No owners found.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Modal */}
      {showModal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{isEditing ? 'Edit Owner' : 'Add Owner'}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                />
              </div>
              <div className="modal-body">
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label htmlFor="username" className="form-label">Username</label>
                    <input
                      type="text"
                      className="form-control"
                      id="username"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="password" className="form-label">Password</label>
                    <div className="input-group">
                      <input
                        type={showPassword[0] ? 'text' : 'password'}
                        className="form-control"
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        required={!isEditing}
                      />
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setShowPassword(prev => ({ ...prev, 0: !prev[0] }))}
                      >
                        {showPassword[0] ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>
                  <div className="mb-3">
                    <label htmlFor="business_name" className="form-label">Business Name</label>
                    <input
                      type="text"
                      className="form-control"
                      id="business_name"
                      name="business_name"
                      value={formData.business_name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="phone" className="form-label">Phone</label>
                    <input
                      type="text"
                      className="form-control"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                    />
                  </div>
                  <button type="submit" className="btn btn-success">{isEditing ? 'Update' : 'Submit'}</button>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;
